import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { b2bGetContact, b2bUpdateContact, b2bDeleteContact, b2bAddContactNote, b2bGetTemplates, b2bSendEmail, b2bHaltContact, b2bGetAccounts, b2bClassifyContact, b2bGetContactOptions, b2bGetSendPreview, adminGetContactVisits, adminGetVisitorPageviews, b2bGetContactChats, b2bGetContactSurveys, chatGetSession } from '../../../lib/adminApi';
import AdminSidebar from '../../../components/AdminSidebar';

const NAVY = '#0a2540'; const ORANGE = '#F37428'; const BLUE = '#0176CE';

function fmtRev(n) {
  if (!n) return '—';
  if (n >= 1e9) return '$' + (n / 1e9).toFixed(1) + 'B';
  if (n >= 1e6) return '$' + (n / 1e6).toFixed(1) + 'M';
  return '$' + (n / 1e3).toFixed(0) + 'K';
}

export default function B2BContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [standardTitles, setStandardTitles] = useState([]);
  const [showSend, setShowSend] = useState(false);
  const [sendMode, setSendMode] = useState('template'); // 'template' | 'custom'
  const [sendForm, setSendForm] = useState({ template_id: '', from_name: 'MasterFileAI', from_email: 'karl@masterfileai.com', subject_override: '', custom_subject: '', custom_body: '' });
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('activity');
  const [classifying, setClassifying] = useState(false);
  const [previewEmail, setPreviewEmail] = useState(null); // { subject, body_html }
  const [webSessions, setWebSessions]   = useState([]);
  const [webLoading, setWebLoading]     = useState(false);
  const [webExpanded, setWebExpanded]   = useState(null);
  const [webPageviews, setWebPageviews] = useState({});
  const [chatSessions, setChatSessions] = useState([]);
  const [chatLoading, setChatLoading]   = useState(false);
  const [chatThread, setChatThread]     = useState(null);
  const [chatThreadData, setChatThreadData] = useState(null);
  const [surveySends, setSurveySends]   = useState([]);
  const [surveyLoading, setSurveyLoading] = useState(false);
  const [surveyExpanded, setSurveyExpanded] = useState(null);

  function load() {
    setLoading(true);
    b2bGetContact(id).then(r => { setData(r); setForm(r.contact); setLoading(false); }).catch(() => setLoading(false));
  }

  useEffect(() => { load(); loadWebVisits(); }, [id]);
  useEffect(() => {
    b2bGetTemplates().then(r => setTemplates(r.templates || [])).catch(() => {});
    b2bGetAccounts().then(r => setAccounts(r.accounts || [])).catch(() => {});
    b2bGetContactOptions().then(r => setStandardTitles(r.standard_titles || [])).catch(() => {});
  }, []);

  async function handleSave(e) {
    e.preventDefault(); setSaving(true);
    try { await b2bUpdateContact(id, { ...form, account_id: form.account_id ? parseInt(form.account_id) : null }); setEditing(false); load(); }
    catch (err) { alert(err.message); }
    setSaving(false);
  }

  async function handleNote(e) {
    e.preventDefault(); if (!note.trim()) return;
    setAddingNote(true);
    try { await b2bAddContactNote(id, note); setNote(''); load(); }
    catch (err) { alert(err.message); }
    setAddingNote(false);
  }

  async function handleReclassify() {
    setClassifying(true);
    try { await b2bClassifyContact(id); load(); }
    catch (err) { alert(err.message); }
    setClassifying(false);
  }

  async function loadWebVisits() {
    setWebLoading(true);
    try {
      const d = await adminGetContactVisits(id);
      setWebSessions(d.sessions || []);
    } catch (_) {}
    finally { setWebLoading(false); }
  }

  async function loadChats() {
    setChatLoading(true);
    try { const d = await b2bGetContactChats(id); setChatSessions(d.sessions || []); }
    catch (_) {} finally { setChatLoading(false); }
  }

  async function loadSurveys() {
    setSurveyLoading(true);
    try { const d = await b2bGetContactSurveys(id); setSurveySends(d.surveys || []); }
    catch (_) {} finally { setSurveyLoading(false); }
  }

  async function openChatThread(sess) {
    setChatThread(sess);
    setChatThreadData(null);
    try { const d = await chatGetSession(sess.session_id); setChatThreadData(d); }
    catch (_) { setChatThreadData({ error: 'Failed to load' }); }
  }

  async function toggleWebSession(sid) {
    if (webExpanded === sid) { setWebExpanded(null); return; }
    setWebExpanded(sid);
    if (!webPageviews[sid]) {
      try {
        const d = await adminGetVisitorPageviews(sid);
        setWebPageviews(p => ({ ...p, [sid]: d.pageviews || [] }));
      } catch (_) {}
    }
  }

  async function handleSend(e) {
    e.preventDefault(); setSending(true);
    try {
      const payload = { contact_id: parseInt(id), from_name: sendForm.from_name, from_email: sendForm.from_email };
      if (sendMode === 'template') {
        payload.template_id = parseInt(sendForm.template_id);
        if (sendForm.subject_override) payload.subject_override = sendForm.subject_override;
      } else {
        payload.custom_subject = sendForm.custom_subject;
        payload.custom_body = sendForm.custom_body.replace(/\n/g, '<br>');
      }
      await b2bSendEmail(payload);
      setShowSend(false); load();
    } catch (err) { alert(err.message); }
    setSending(false);
  }

  if (loading) return <div style={{ display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" }}><AdminSidebar active="b2b-contacts" /><main style={{ padding: 40, color: '#64748b' }}>Loading…</main></div>;
  if (!data) return <div style={{ display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" }}><AdminSidebar active="b2b-contacts" /><main style={{ padding: 40, color: '#ef4444' }}>Not found</main></div>;

  const c = data.contact;
  const fullName = [c.first_name, c.last_name].filter(Boolean).join(' ') || 'Contact';
  const lastEmail = data.sends?.[0];
  const linkedinHref = c.linkedin_url ? (c.linkedin_url.startsWith('http') ? c.linkedin_url : `https://${c.linkedin_url}`) : null;

  return (
    <div style={s.page}>
      <AdminSidebar active="b2b-contacts" />
      <main style={s.main}>
        <button style={s.backBtn} onClick={() => navigate('/admin/b2b/contacts')}>← Contacts</button>

        {/* Company context banner */}
        {c.account_id && (
          <div style={s.companyBanner}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Link to={`/admin/b2b/accounts/${c.account_id}`} style={{ color: NAVY, fontWeight: 700, fontSize: 16, textDecoration: 'none' }}>{c.company_name || '—'}</Link>
                {c.website && (
                  <a href={c.website.startsWith('http') ? c.website : `https://${c.website}`} target="_blank" rel="noreferrer"
                    style={{ color: BLUE, fontSize: 12, display: 'flex', alignItems: 'center', gap: 3 }}>
                    🌐 {c.website}
                  </a>
                )}
              </div>
              {(c.company_city || c.company_state) && (
                <span style={{ color: '#64748b', fontSize: 12 }}>📍 {[c.company_city, c.company_state].filter(Boolean).join(', ')}</span>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
              {[
                { label: 'Industry',  value: c.industry },
                { label: 'Revenue',   value: c.annual_revenue ? fmtRev(c.annual_revenue) : null, green: true },
                { label: 'Employees', value: c.employee_count ? c.employee_count.toLocaleString() : null },
                { label: 'ERP',       value: c.erp_system },
              ].map(({ label, value, green }) => (
                <div key={label} style={s.bannerCard}>
                  <div style={s.bannerKey}>{label}</div>
                  <div style={{ color: green ? '#059669' : NAVY, fontWeight: 600, fontSize: 13 }}>{value || '—'}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contact header */}
        <div style={s.header}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 2 }}>
              <h1 style={s.title}>{fullName}</h1>
              <span style={{ background: c.status === 'active' ? 'rgba(5,150,105,0.08)' : 'rgba(239,68,68,0.08)', color: c.status === 'active' ? '#059669' : '#dc2626', borderRadius: 4, fontSize: 10, fontWeight: 600, padding: '3px 7px' }}>{c.status}</span>
              {c.unsubscribed_at && (
                <span style={{ background: 'rgba(239,68,68,0.08)', color: '#dc2626', borderRadius: 4, fontSize: 10, fontWeight: 600, padding: '3px 7px', display: 'flex', alignItems: 'center', gap: 3 }}>
                  UNSUBSCRIBED {new Date(c.unsubscribed_at).toLocaleDateString()}
                </span>
              )}
              {lastEmail && (
                <span style={{ background: 'rgba(243,116,40,0.08)', color: ORANGE, borderRadius: 4, fontSize: 11, fontWeight: 500, padding: '3px 8px' }}>
                  Last email {new Date(lastEmail.sent_on_utc).toLocaleDateString()}
                </span>
              )}
            </div>
            {c.title && <div style={{ color: '#64748b', fontSize: 13, marginBottom: 6 }}>{c.title}</div>}
            {c.standard_title && (
              <span style={{ background: 'rgba(1,118,206,0.08)', color: BLUE, borderRadius: 4, fontSize: 11, fontWeight: 600, padding: '2px 8px', display: 'inline-block', marginBottom: 12 }}>
                {c.standard_title}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <button style={s.primaryBtn} onClick={() => setShowSend(true)}>Send Email</button>
            <button style={s.editBtn} onClick={() => setEditing(e => !e)}>{editing ? 'Cancel' : 'Edit'}</button>
            {c.title && (
              <button style={s.editBtn} onClick={handleReclassify} disabled={classifying}>
                {classifying ? 'Classifying…' : '✦ Re-classify'}
              </button>
            )}
            <button style={s.deleteBtn} onClick={async () => { if (confirm('Delete contact?')) { await b2bDeleteContact(id); navigate('/admin/b2b/contacts'); } }}>Delete</button>
          </div>
        </div>

        {/* Contact info cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
          <div style={s.infoCard}>
            <div style={s.infoKey}>Email</div>
            {c.email
              ? <a href={`mailto:${c.email}`} style={{ color: BLUE, fontSize: 13, wordBreak: 'break-all', textDecoration: 'none' }}>{c.email}</a>
              : <span style={s.infoEmpty}>—</span>}
          </div>
          <div style={s.infoCard}>
            <div style={s.infoKey}>Phone</div>
            {c.phone
              ? <a href={`tel:${c.phone}`} style={{ color: NAVY, fontSize: 13, textDecoration: 'none', fontWeight: 500 }}>{c.phone}</a>
              : <span style={s.infoEmpty}>—</span>}
          </div>
          <div style={s.infoCard}>
            <div style={s.infoKey}>LinkedIn</div>
            {linkedinHref
              ? <a href={linkedinHref} target="_blank" rel="noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="#0077B5"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  <span style={{ color: BLUE, fontSize: 13 }}>View Profile</span>
                </a>
              : <span style={s.infoEmpty}>—</span>}
          </div>
          <div style={s.infoCard}>
            <div style={s.infoKey}>Added</div>
            <span style={{ color: NAVY, fontSize: 13, fontWeight: 500 }}>{new Date(c.created_on_utc).toLocaleDateString()}</span>
          </div>
        </div>

        {editing && (
          <div style={{ ...s.card, marginBottom: 24 }}>
            <form onSubmit={handleSave} style={{ display: 'grid', gap: 12, padding: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <label style={s.label}>First Name<input style={s.inp} value={form.first_name || ''} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} /></label>
                <label style={s.label}>Last Name<input style={s.inp} value={form.last_name || ''} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} /></label>
                <label style={s.label}>Email<input style={s.inp} type="email" value={form.email || ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></label>
                <label style={s.label}>Title<input style={s.inp} value={form.title || ''} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} /></label>
                <label style={s.label}>Phone<input style={s.inp} value={form.phone || ''} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} /></label>
                <label style={s.label}>Account
                  <select style={s.inp} value={form.account_id || ''} onChange={e => setForm(f => ({ ...f, account_id: e.target.value }))}>
                    <option value="">No Account</option>
                    {accounts.map(a => <option key={a.account_id} value={a.account_id}>{a.company_name}</option>)}
                  </select>
                </label>
                <label style={s.label}>LinkedIn<input style={s.inp} value={form.linkedin_url || ''} onChange={e => setForm(f => ({ ...f, linkedin_url: e.target.value }))} /></label>
                <label style={s.label}>Standard Title (Category)
                  <select style={s.inp} value={form.standard_title || ''} onChange={e => setForm(f => ({ ...f, standard_title: e.target.value || null }))}>
                    <option value="">— unclassified —</option>
                    {standardTitles.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
                <label style={s.label}>Status
                  <select style={s.inp} value={form.status || 'active'} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                    <option value="active">Active</option>
                    <option value="unsubscribed">Unsubscribed</option>
                    <option value="bounced">Bounced</option>
                  </select>
                </label>
              </div>
              <button type="submit" style={{ ...s.primaryBtn, width: 'fit-content' }} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </form>
          </div>
        )}

        {/* Marketing job enrollments */}
        {data.enrollments.length > 0 && (
          <div style={{ ...s.card, marginBottom: 24 }}>
            <div style={s.cardHeader}><span style={s.cardTitle}>Marketing Jobs</span></div>
            <div style={{ padding: '8px 0' }}>
              {data.enrollments.map(e => (
                <div key={e.enrollment_id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 20px', borderBottom: '1px solid #f1f5f9' }}>
                  <Link to={`/admin/b2b/jobs/${e.job_id}`} style={{ color: NAVY, fontSize: 13, fontWeight: 500, flex: 1, textDecoration: 'none' }}>{e.job_name}</Link>
                  <span style={{ color: '#64748b', fontSize: 12 }}>Step {e.current_step}</span>
                  <span style={{ background: e.status === 'active' ? 'rgba(5,150,105,0.08)' : e.status === 'halted' ? 'rgba(239,68,68,0.08)' : 'rgba(100,116,139,0.08)', color: e.status === 'active' ? '#059669' : e.status === 'halted' ? '#dc2626' : '#64748b', borderRadius: 4, fontSize: 10, fontWeight: 600, padding: '2px 6px', textTransform: 'uppercase' }}>{e.status}</span>
                  {e.status === 'active' && (
                    <button style={{ background: 'rgba(239,68,68,0.08)', border: 'none', borderRadius: 6, color: '#dc2626', cursor: 'pointer', fontSize: 12, padding: '4px 10px' }}
                      onClick={async () => { if (confirm('Halt this contact from the job?')) { await b2bHaltContact(e.job_id, c.contact_id); load(); } }}>
                      Halt
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div style={s.tabs}>
          {['activity', 'notes'].map(tab => (
            <button key={tab} style={{ ...s.tab, ...(activeTab === tab ? s.tabActive : {}) }} onClick={() => setActiveTab(tab)}>
              {tab === 'activity' ? `Email Activity (${data.sends.length})` : `Notes (${data.notes.length})`}
            </button>
          ))}
          <button onClick={() => setActiveTab('web')}
            style={{ ...s.tab, borderBottom: activeTab === 'web' ? `2px solid ${ORANGE}` : '2px solid transparent',
              color: activeTab === 'web' ? NAVY : '#64748b' }}>
            Web Visits {webSessions.length > 0 ? `(${webSessions.length})` : ''}
          </button>
          <button onClick={() => { setActiveTab('chats'); if (!chatSessions.length) loadChats(); }}
            style={{ ...s.tab, borderBottom: activeTab === 'chats' ? `2px solid ${ORANGE}` : '2px solid transparent',
              color: activeTab === 'chats' ? NAVY : '#64748b' }}>
            Chats {chatSessions.length > 0 ? `(${chatSessions.length})` : ''}
          </button>
          <button onClick={() => { setActiveTab('surveys'); if (!surveySends.length) loadSurveys(); }}
            style={{ ...s.tab, borderBottom: activeTab === 'surveys' ? `2px solid ${ORANGE}` : '2px solid transparent',
              color: activeTab === 'surveys' ? NAVY : '#64748b' }}>
            Surveys {surveySends.length > 0 ? `(${surveySends.length})` : ''}
          </button>
        </div>

        {activeTab === 'activity' && (
          <div style={s.card}>
            <table style={s.table}>
              <thead>
                <tr>{['Date', 'Subject', 'Job', 'Step', 'Opens', 'Clicks', 'Manual', ''].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {data.sends.length === 0 && <tr><td colSpan={8} style={s.empty}>No emails sent yet</td></tr>}
                {data.sends.map(em => (
                  <tr key={em.send_id} style={s.tr}>
                    <td style={{ ...s.td, color: '#94a3b8', fontSize: 12 }}>{new Date(em.sent_on_utc).toLocaleDateString()}</td>
                    <td style={{ ...s.td, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{em.subject}</td>
                    <td style={s.td}>{em.job_name ? <Link to={`/admin/b2b/jobs/${em.job_id}`} style={{ color: BLUE, textDecoration: 'none', fontSize: 12 }}>{em.job_name}</Link> : '—'}</td>
                    <td style={{ ...s.td, textAlign: 'center' }}>{em.step_number || '—'}</td>
                    <td style={{ ...s.td, textAlign: 'center' }}>{em.open_count > 0 ? <span style={{ color: '#059669', fontWeight: 600 }}>{em.open_count}</span> : '0'}</td>
                    <td style={{ ...s.td, textAlign: 'center' }}>{em.click_count > 0 ? <span style={{ color: BLUE, fontWeight: 600 }}>{em.click_count}</span> : '0'}</td>
                    <td style={{ ...s.td, textAlign: 'center' }}>{em.is_manual ? '✓' : ''}</td>
                    <td style={s.td}>
                      <button style={{ background: 'none', border: 'none', color: BLUE, cursor: 'pointer', fontSize: 12, fontWeight: 500, padding: 0 }}
                        onClick={async () => {
                          const r = await b2bGetSendPreview(em.send_id);
                          setPreviewEmail({ subject: em.subject, body_html: r.body_html });
                        }}>View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'notes' && (
          <div>
            <form onSubmit={handleNote} style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
              <textarea style={{ ...s.inp, flex: 1, height: 70, resize: 'vertical' }} placeholder="Add a note…" value={note} onChange={e => setNote(e.target.value)} />
              <button type="submit" style={{ ...s.primaryBtn, alignSelf: 'flex-end' }} disabled={addingNote}>{addingNote ? 'Saving…' : 'Add Note'}</button>
            </form>
            <div style={s.card}>
              {data.notes.length === 0 && <div style={s.empty}>No notes yet</div>}
              {data.notes.map(n => (
                <div key={n.note_id} style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ color: NAVY, fontSize: 12, fontWeight: 600 }}>{n.admin_name || 'Admin'}</span>
                    <span style={{ color: '#94a3b8', fontSize: 11 }}>{new Date(n.created_on_utc).toLocaleString()}</span>
                  </div>
                  <div style={{ color: '#374151', fontSize: 13, lineHeight: 1.6 }}>{n.body}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'web' && (
          <div>
            {webLoading ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>Loading…</div>
            ) : webSessions.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No website visits recorded</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['Date', 'Landing Page', 'Referrer', 'Pages', 'Duration', 'Location', 'Type'].map(h => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left', color: '#64748b',
                        fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
                        borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {webSessions.map(s => (<>
                    <tr key={s.session_id}
                      onClick={() => toggleWebSession(s.session_id)}
                      style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
                        background: webExpanded === s.session_id ? '#fafbff' : '#fff' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = webExpanded === s.session_id ? '#fafbff' : '#fff'}
                    >
                      <td style={{ padding: '10px 16px', color: '#475569', whiteSpace: 'nowrap' }}>
                        {new Date(s.first_seen_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </td>
                      <td style={{ padding: '10px 16px', color: '#0176CE', maxWidth: 180,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.landing_page || '—'}</td>
                      <td style={{ padding: '10px 16px', color: '#94a3b8', fontSize: 12 }}>
                        {s.referrer ? (() => { try { return new URL(s.referrer).hostname.replace('www.',''); } catch { return s.referrer; } })() : 'Direct'}
                      </td>
                      <td style={{ padding: '10px 16px', color: '#475569', textAlign: 'center' }}>{s.page_count}</td>
                      <td style={{ padding: '10px 16px', color: '#475569', whiteSpace: 'nowrap' }}>
                        {s.last_seen_at && s.first_seen_at ? (() => {
                          const sec = Math.round((new Date(s.last_seen_at) - new Date(s.first_seen_at)) / 1000);
                          if (sec < 60) return `${sec}s`;
                          return `${Math.floor(sec/60)}m ${sec%60}s`;
                        })() : '—'}
                      </td>
                      <td style={{ padding: '10px 16px', color: '#475569' }}>
                        {[s.city, s.country].filter(Boolean).join(', ') || '—'}
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        {s.is_return
                          ? <span style={{ background: 'rgba(5,150,105,0.08)', color: '#059669', borderRadius: 5, fontSize: 10, fontWeight: 700, padding: '2px 8px' }}>RETURN</span>
                          : <span style={{ background: 'rgba(1,118,206,0.08)', color: '#0176CE', borderRadius: 5, fontSize: 10, fontWeight: 700, padding: '2px 8px' }}>NEW</span>}
                      </td>
                    </tr>
                    {webExpanded === s.session_id && (
                      <tr key={`${s.session_id}-pv`}>
                        <td colSpan={7} style={{ background: '#f8fafc', padding: '0 0 0 32px', borderBottom: '2px solid #e2e8f0' }}>
                          {!webPageviews[s.session_id] ? (
                            <div style={{ padding: 12, color: '#94a3b8', fontSize: 12 }}>Loading…</div>
                          ) : webPageviews[s.session_id].length === 0 ? (
                            <div style={{ padding: 12, color: '#94a3b8', fontSize: 12 }}>No page data</div>
                          ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                              <thead>
                                <tr>
                                  {['#', 'Page', 'Title', 'Time on Page', 'Viewed At'].map(h => (
                                    <th key={h} style={{ padding: '7px 14px', textAlign: 'left', color: '#94a3b8',
                                      fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {webPageviews[s.session_id].map((pv, i) => (
                                  <tr key={pv.pageview_id} style={{ borderTop: '1px solid #e9edf2' }}>
                                    <td style={{ padding: '8px 14px', color: '#94a3b8' }}>{i + 1}</td>
                                    <td style={{ padding: '8px 14px', color: '#0176CE', fontWeight: 500 }}>{pv.page_url}</td>
                                    <td style={{ padding: '8px 14px', color: '#64748b' }}>{pv.page_title || '—'}</td>
                                    <td style={{ padding: '8px 14px', color: '#475569' }}>
                                      {pv.time_on_page == null ? '—' : pv.time_on_page < 60 ? `${pv.time_on_page}s` : `${Math.floor(pv.time_on_page/60)}m ${pv.time_on_page%60}s`}
                                    </td>
                                    <td style={{ padding: '8px 14px', color: '#475569', whiteSpace: 'nowrap' }}>
                                      {new Date(pv.viewed_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </>))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ── Chats tab ── */}
        {activeTab === 'chats' && (
          <div style={s.card}>
            {chatLoading ? (
              <div style={s.empty}>Loading…</div>
            ) : chatSessions.length === 0 ? (
              <div style={s.empty}>No chat sessions found for this contact</div>
            ) : (
              <table style={s.table}>
                <thead>
                  <tr>{['Date', 'Page', 'Messages', 'Errors', 'Lead', 'Last Activity', ''].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {chatSessions.map((cs, i) => (
                    <tr key={cs.session_id} style={s.tr}>
                      <td style={{ ...s.td, color: '#94a3b8', fontSize: 12, whiteSpace: 'nowrap' }}>{new Date(cs.started_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</td>
                      <td style={s.td}>{cs.page === '/' ? 'Home' : (cs.page || '—').replace(/^\//, '')}</td>
                      <td style={{ ...s.td, textAlign: 'center', fontWeight: 600 }}>{cs.message_count}</td>
                      <td style={{ ...s.td, textAlign: 'center' }}>{cs.error_count > 0 ? <span style={{ color: '#b91c1c', fontWeight: 600 }}>{cs.error_count}</span> : <span style={{ color: '#10b981' }}>0</span>}</td>
                      <td style={{ ...s.td, textAlign: 'center' }}>{cs.lead_captured ? <span style={{ color: ORANGE, fontWeight: 700 }}>✓</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                      <td style={{ ...s.td, color: '#94a3b8', fontSize: 12, whiteSpace: 'nowrap' }}>{cs.last_message_at ? new Date(cs.last_message_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}</td>
                      <td style={s.td}>
                        <button onClick={() => openChatThread(cs)}
                          style={{ background: NAVY, border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 12, color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
                          View Thread →
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ── Surveys tab ── */}
        {activeTab === 'surveys' && (
          <div style={s.card}>
            {surveyLoading ? (
              <div style={s.empty}>Loading…</div>
            ) : surveySends.length === 0 ? (
              <div style={s.empty}>No surveys sent to this contact yet</div>
            ) : (
              <table style={s.table}>
                <thead>
                  <tr>{['Survey', 'Sent', 'Completed', 'Answers', ''].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {surveySends.map(sv => {
                    const answers = sv.answers_json ? JSON.parse(sv.answers_json) : [];
                    return (
                      <>
                        <tr key={sv.send_id} style={s.tr}>
                          <td style={{ ...s.td, fontWeight: 600 }}>{sv.survey_title}</td>
                          <td style={{ ...s.td, color: '#94a3b8', fontSize: 12 }}>{sv.sent_at ? new Date(sv.sent_at).toLocaleDateString() : '—'}</td>
                          <td style={s.td}>
                            {sv.completed_at
                              ? <span style={{ color: '#059669', fontWeight: 600 }}>{new Date(sv.completed_at).toLocaleDateString()}</span>
                              : <span style={{ color: '#94a3b8' }}>Not completed</span>}
                          </td>
                          <td style={{ ...s.td, textAlign: 'center' }}>{answers.length || '—'}</td>
                          <td style={s.td}>
                            {answers.length > 0 && (
                              <button onClick={() => setSurveyExpanded(surveyExpanded === sv.send_id ? null : sv.send_id)}
                                style={{ background: 'none', border: 'none', color: BLUE, cursor: 'pointer', fontSize: 12, fontWeight: 500, padding: 0 }}>
                                {surveyExpanded === sv.send_id ? 'Hide' : 'View Answers'}
                              </button>
                            )}
                          </td>
                        </tr>
                        {surveyExpanded === sv.send_id && (
                          <tr key={`${sv.send_id}-answers`}>
                            <td colSpan={5} style={{ background: '#f8fafc', padding: '12px 24px', borderBottom: '2px solid #e2e8f0' }}>
                              {answers.map((a, i) => (
                                <div key={i} style={{ marginBottom: 10 }}>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: NAVY, marginBottom: 3 }}>{a.question_text}</div>
                                  <div style={{ fontSize: 13, color: '#374151' }}>{a.answer_text || (a.answer_numeric != null ? a.answer_numeric : '—')}</div>
                                </div>
                              ))}
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ── Chat thread modal ── */}
        {chatThread && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: 24 }}
            onClick={() => setChatThread(null)}>
            <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 640, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.2)' }}
              onClick={e => e.stopPropagation()}>
              <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>Chat Thread</div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                    {new Date(chatThread.started_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} · {chatThread.message_count} messages
                  </div>
                </div>
                <button onClick={() => setChatThread(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 20, cursor: 'pointer' }}>✕</button>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                {!chatThreadData && <div style={{ textAlign: 'center', color: '#94a3b8', padding: 32 }}>Loading…</div>}
                {chatThreadData?.messages?.map((msg, i) => (
                  <div key={i}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 5 }}>
                      <div style={{ background: ORANGE, color: '#fff', borderRadius: '14px 14px 4px 14px', padding: '8px 12px', fontSize: 13, maxWidth: '80%', lineHeight: 1.5 }}>{msg.user_message}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flexShrink: 0, marginTop: 2 }}>🤖</div>
                      <div style={{ background: '#f1f5f9', borderRadius: '4px 14px 14px 14px', padding: '8px 12px', fontSize: 13, maxWidth: '80%', lineHeight: 1.5, color: '#1e293b' }}>{msg.bot_reply}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {showSend && (
          <div style={s.modal}>
            <div style={{ ...s.modalBox, width: 560 }}>
              <h2 style={{ margin: '0 0 16px', color: NAVY, fontSize: 18 }}>Send Email to {fullName}</h2>

              {/* Mode toggle */}
              <div style={{ display: 'flex', gap: 0, marginBottom: 16, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                {[['template', 'Use Template'], ['custom', 'Write Custom Email']].map(([mode, label]) => (
                  <button key={mode} type="button"
                    style={{ flex: 1, padding: '8px 0', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', background: sendMode === mode ? NAVY : '#f8fafc', color: sendMode === mode ? '#fff' : '#64748b' }}
                    onClick={() => setSendMode(mode)}>{label}</button>
                ))}
              </div>

              <form onSubmit={handleSend} style={{ display: 'grid', gap: 12 }}>
                {sendMode === 'template' ? (<>
                  <label style={s.label}>Template *
                    <select required style={s.inp} value={sendForm.template_id} onChange={e => setSendForm(f => ({ ...f, template_id: e.target.value }))}>
                      <option value="">Select template…</option>
                      {templates.map(t => <option key={t.template_id} value={t.template_id}>{t.template_name}</option>)}
                    </select>
                  </label>
                  <label style={s.label}>Subject Override (optional)
                    <input style={s.inp} placeholder="Leave blank to use template subject" value={sendForm.subject_override} onChange={e => setSendForm(f => ({ ...f, subject_override: e.target.value }))} />
                  </label>
                </>) : (<>
                  <label style={s.label}>Subject *
                    <input required style={s.inp} placeholder="Email subject line" value={sendForm.custom_subject} onChange={e => setSendForm(f => ({ ...f, custom_subject: e.target.value }))} />
                  </label>
                  <label style={s.label}>Message *
                    <textarea required style={{ ...s.inp, height: 160, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
                      placeholder={`Hi ${c.first_name},\n\nWrite your message here…`}
                      value={sendForm.custom_body} onChange={e => setSendForm(f => ({ ...f, custom_body: e.target.value }))} />
                  </label>
                  <p style={{ margin: 0, fontSize: 11, color: '#94a3b8' }}>Plain text — line breaks become &lt;br&gt; tags. Variables like {'{{first_name}}'} still work.</p>
                </>)}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <label style={s.label}>From Name *<input required style={s.inp} value={sendForm.from_name} onChange={e => setSendForm(f => ({ ...f, from_name: e.target.value }))} /></label>
                  <label style={s.label}>From Email *<input required style={s.inp} type="email" value={sendForm.from_email} onChange={e => setSendForm(f => ({ ...f, from_email: e.target.value }))} /></label>
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button type="button" style={s.cancelBtn} onClick={() => setShowSend(false)}>Cancel</button>
                  <button type="submit" style={s.primaryBtn} disabled={sending}>{sending ? 'Sending…' : 'Send'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Email preview modal */}
        {previewEmail && (
          <div style={s.modal} onClick={() => setPreviewEmail(null)}>
            <div style={{ background: '#fff', borderRadius: 12, width: '780px', maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
              onClick={e => e.stopPropagation()}>
              {/* Email client header */}
              <div style={{ padding: '16px 24px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontWeight: 700, color: NAVY, fontSize: 15 }}>{previewEmail.subject}</div>
                  <button onClick={() => setPreviewEmail(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: '0 4px' }}>×</button>
                </div>
                <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#64748b' }}>
                  <span><strong>To:</strong> {c.first_name} {c.last_name} &lt;{c.email}&gt;</span>
                  <span><strong>From:</strong> MasterFileAI &lt;karl@masterfileai.com&gt;</span>
                </div>
              </div>
              {/* Email body */}
              <div style={{ flex: 1, overflow: 'auto' }}>
                {previewEmail.body_html
                  ? <iframe srcDoc={previewEmail.body_html} style={{ width: '100%', height: '100%', minHeight: 500, border: 'none' }} title="Email Preview" sandbox="allow-same-origin" />
                  : <div style={{ padding: 40, color: '#94a3b8', textAlign: 'center' }}>No HTML preview available for this email.</div>
                }
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const s = {
  page: { display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" },
  main: { flex: 1, background: '#f0f5fb', padding: '36px 40px', overflowY: 'auto' },
  backBtn: { background: 'transparent', border: 'none', color: BLUE, cursor: 'pointer', fontSize: 13, fontWeight: 500, padding: '0 0 8px', display: 'block' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 },
  title: { color: NAVY, fontSize: 24, fontWeight: 700, margin: '0 0 4px' },
  primaryBtn: { background: ORANGE, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  editBtn: { background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, color: NAVY, cursor: 'pointer', fontSize: 13, padding: '7px 16px' },
  deleteBtn: { background: 'rgba(239,68,68,0.08)', border: 'none', borderRadius: 8, color: '#ef4444', cursor: 'pointer', fontSize: 13, padding: '7px 16px' },
  cancelBtn: { background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, cursor: 'pointer' },
  card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginBottom: 24 },
  cardHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #f1f5f9' },
  cardTitle: { color: NAVY, fontSize: 14, fontWeight: 600 },
  tabs: { display: 'flex', gap: 4, marginBottom: 16 },
  tab: { background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 13, fontWeight: 500, padding: '8px 16px', borderRadius: 8 },
  tabActive: { background: '#fff', color: NAVY, fontWeight: 600, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 14px', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  tr: { borderBottom: '1px solid #f1f5f9' },
  td: { color: '#374151', fontSize: 13, padding: '10px 14px', verticalAlign: 'middle' },
  empty: { color: '#94a3b8', fontSize: 13, padding: '32px', textAlign: 'center' },
  label: { color: '#374151', fontSize: 12, fontWeight: 500, display: 'flex', flexDirection: 'column', gap: 4 },
  inp: { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, width: '100%', boxSizing: 'border-box', marginTop: 2 },
  modal:         { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modalBox:      { background: '#fff', borderRadius: 12, padding: '28px 32px', width: 500, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  companyBanner: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 20px', marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  bannerCard:    { background: '#f8fafc', borderRadius: 8, padding: '10px 14px', border: '1px solid #f1f5f9' },
  bannerKey:     { color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 },
  infoCard:      { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
  infoKey:       { color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 },
  infoEmpty:     { color: '#cbd5e1', fontSize: 13 },
};
